// Type compatibility + coercion helpers for Shopify → Zoho field mappings.
//
// Shopify fields carry a `type` in config/shopifyFields/*.json:
//   "string" | "integer" | "number" | "double" | "boolean" | "date" |
//   "datetime" | "object" | "array"
//
// Zoho fields carry a `data_type` returned by ZOHO.CRM.META.getFields:
//   "text" | "textarea" | "email" | "phone" | "website" | "url" | "picklist" |
//   "multiselectpicklist" | "lookup" | "user" | "autonumber" | "integer" |
//   "bigint" | "double" | "currency" | "percent" | "boolean" | "date" |
//   "datetime" | ...
//
// The goal of this module is twofold:
//  1. `isShopifyCompatibleWithZoho` — drive the field-mapping UI so users
//     cannot pick a Shopify source whose type cannot be coerced to the Zoho
//     target (or vice versa).
//  2. `coerceShopifyToZoho` — a pure function that performs the actual
//     value coercion. Kept here so the client-side mapping UI and the
//     server-side Deluge sync function can share the same ruleset (or at
//     least a single source of truth to port from).

// Per–Zoho-data-type whitelist of Shopify JSON types that are allowed as a
// source for that Zoho target. Rules are strict: a text Zoho field only
// accepts string Shopify fields, an integer Zoho field only accepts numeric
// Shopify fields, etc. This prevents the classic "integer Id → text Title"
// mismatch that breaks Zoho record creation.
export const ZOHO_TO_SHOPIFY_COMPAT = {
  // Text-like: only Shopify strings.
  text: new Set(["string"]),
  textarea: new Set(["string"]),
  email: new Set(["string"]),
  website: new Set(["string"]),
  url: new Set(["string"]),
  phone: new Set(["string"]),
  picklist: new Set(["string"]),
  multiselectpicklist: new Set(["string", "array"]),
  autonumber: new Set(["string"]),

  // Lookup / user fields reference records by ID — accept numeric or string
  // IDs, since Shopify customer/order IDs come through as integers.
  lookup: new Set(["integer", "number", "double", "string"]),
  user: new Set(["integer", "number", "double", "string"]),

  // Numeric: integer / bigint stay strict on numeric sources because Shopify
  // integer fields (IDs, quantities, grams) are always real integers in JSON.
  // The float-style targets (double / currency / percent) ALSO accept strings
  // because Shopify serialises money values as strings ("29.95", "1234.00")
  // even though the Zoho target is numeric — `coerceShopifyToZoho` below
  // parses them via `parseFloat` at sync time.
  integer: new Set(["integer", "number"]),
  bigint: new Set(["integer", "number"]),
  double: new Set(["integer", "number", "double", "string"]),
  currency: new Set(["integer", "number", "double", "string"]),
  percent: new Set(["integer", "number", "double", "string"]),

  // Boolean: bool only.
  boolean: new Set(["boolean"]),

  // Date / datetime: both Shopify date and datetime can feed either Zoho
  // temporal type; we coerce to the right ISO shape at sync time.
  date: new Set(["date", "datetime"]),
  datetime: new Set(["date", "datetime"]),

  // Subform / list (e.g. Product_Details, custom subforms): only Shopify
  // array sources are valid since the parent itself is a list of records.
  // The actual field-by-field mapping happens in a nested block.
  subform: new Set(["array"]),
  list: new Set(["array"]),
};

// ID-like Zoho field names (e.g. "Shopify_ID_EXT", "Customer_ID", "Order Id")
// accept both integer and string Shopify sources, since Shopify IDs travel as
// integers on REST and as string GIDs on GraphQL.
const ID_LIKE_RE = /(^|[^A-Za-z])ID([^A-Za-z]|$)|shopify[_ ]?id/i;

export const isIdLikeZohoField = (zohoApiName) =>
  typeof zohoApiName === "string" && ID_LIKE_RE.test(zohoApiName);

// ID-like Shopify field names — anything ending in `id` (with optional `[]`
// for array sub-fields). e.g. "id", "customer.id", "line_items[].id",
// "variants[].id". When the Shopify side is an ID, the user may legitimately
// want to store it in a text/string Zoho field for tracking.
const SHOPIFY_ID_RE = /(?:^|[._\\[])id$/i;

export const isIdLikeShopifyField = (shopifyApiName) =>
  typeof shopifyApiName === "string" && SHOPIFY_ID_RE.test(shopifyApiName);

// Zoho data_types that can plausibly hold a Shopify ID — covers the common
// cases (text, picklists, integer/bigint, lookup, autonumber). Picklists are
// included because users sometimes store IDs in ad-hoc picklists.
const ID_FRIENDLY_ZOHO_TYPES = new Set([
  "text",
  "textarea",
  "string",
  "integer",
  "bigint",
  "double",
  "currency",
  "lookup",
  "user",
  "autonumber",
  "picklist",
  "phone",
  "email",
  "url",
  "website",
]);

/**
 * Return true iff a Shopify field of the given `type` can be coerced into a
 * Zoho field of the given `data_type`. Missing type info on either side is
 * treated as "unknown → allow" so legacy mappings and unseen Zoho data_types
 * never get silently hidden from the UI.
 *
 * Relaxations:
 *   - `zohoApiName` is an ID-like field → both integer and string Shopify
 *     types are accepted.
 *   - `shopifyApiName` is an ID-like path (ends with `id`) → most Zoho
 *     scalar types are accepted, since users often store IDs in custom
 *     text fields.
 */
export const isShopifyCompatibleWithZoho = (
  shopifyType,
  zohoDataType,
  zohoApiName,
  shopifyApiName
) => {
  if (!shopifyType || !zohoDataType) return true;
  if (isIdLikeZohoField(zohoApiName) && (shopifyType === "integer" || shopifyType === "string")) {
    return true;
  }
  if (isIdLikeShopifyField(shopifyApiName) && ID_FRIENDLY_ZOHO_TYPES.has(zohoDataType)) {
    return true;
  }
  const allowed = ZOHO_TO_SHOPIFY_COMPAT[zohoDataType];
  if (!allowed) return true; // unknown Zoho type → don't over-filter
  return allowed.has(shopifyType);
};

/** Format a Date (or ISO string) as YYYY-MM-DD in UTC. */
const toIsoDate = (value) => {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

/** Format a Date (or ISO string) as a full ISO 8601 string. */
const toIsoDateTime = (value) => {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

/** Parse a Shopify boolean-ish value into a real boolean. */
const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true" || v === "1" || v === "yes") return true;
    if (v === "false" || v === "0" || v === "no" || v === "") return false;
  }
  return null;
};

/**
 * Coerce a raw Shopify value into a shape the Zoho API will accept for a
 * field of `zohoDataType`. Returns `null` for missing/invalid input so the
 * caller can decide whether to omit the field or send null.
 */
export const coerceShopifyToZoho = (value, zohoDataType) => {
  if (value === undefined || value === null) return null;

  switch (zohoDataType) {
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "website":
    case "url":
    case "picklist":
    case "lookup":
    case "user":
    case "autonumber":
      return String(value);

    case "multiselectpicklist": {
      if (Array.isArray(value)) return value.map(String);
      if (typeof value === "string") {
        return value.split(",").map((s) => s.trim()).filter(Boolean);
      }
      return [String(value)];
    }

    case "integer":
    case "bigint": {
      const n = parseInt(value, 10);
      return Number.isFinite(n) ? n : null;
    }

    case "double":
    case "currency":
    case "percent": {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : null;
    }

    case "boolean":
      return toBoolean(value);

    case "date":
      return toIsoDate(value);

    case "datetime":
      return toIsoDateTime(value);

    default:
      return value;
  }
};
